import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPermission } from "@/lib/permissions";
import { GlossaryTerm } from "@/components/GlossaryTerm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, ReferenceLine,
} from "recharts";
import {
  TrendingUp, GitBranch, Rewind, ShieldCheck, Info, Gauge, Zap, AlertTriangle,
  ArrowUpRight, ArrowDownRight, Minus, Clock, Save, Network, History, ChevronRight, Pin,
} from "lucide-react";

const fmtGBP = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e6) return `£${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `£${(n / 1e3).toFixed(0)}k`;
  return `£${Math.round(n)}`;
};

// ---------------------------------------------------------------------------
// Trust envelope — shown on every Foresight output (the credibility layer)
// ---------------------------------------------------------------------------
const TrustEnvelope = ({ trust, testid }) => {
  const [open, setOpen] = useState(false);
  if (!trust) return null;
  const tone =
    trust.confidence >= 80 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : trust.confidence >= 60 ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-orange-700 bg-orange-50 border-orange-200";
  return (
    <div className="mt-4 rounded-md border border-black/5 bg-ek-canvas/60 p-3" data-testid={testid}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-ek-ink/70">
          <ShieldCheck size={14} className="text-ek-purple" />
          Trust &amp; assumptions
          <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${tone}`}>
            {trust.confidence_label} confidence · {trust.confidence}%
          </span>
        </span>
        <Info size={14} className="text-ek-ink/30" />
      </button>
      {open && (
        <div className="mt-3 space-y-2 text-xs text-ek-ink/60">
          <div>
            <p className="font-semibold text-ek-ink/70">Key assumptions</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {trust.assumptions.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
          <div>
            <p className="font-semibold text-ek-ink/70">Sources</p>
            <p className="mt-0.5">{trust.sources.join(" · ")}</p>
          </div>
          <p className="rounded bg-ek-purple/5 px-2 py-1.5 italic text-ek-ink/65">{trust.disclaimer}</p>
        </div>
      )}
    </div>
  );
};

// A monetary figure is always shown as a distribution, never a single point.
const MoneyRange = ({ label, money, accent = "text-ek-ink", testid, tipKey }) => (
  <div className="rounded-md border border-black/5 bg-white p-4" data-testid={testid}>
    <p className="text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">
      {tipKey ? <GlossaryTerm k={tipKey} label={label} /> : label}
    </p>
    <p className={`mt-1 font-display text-2xl font-bold ${accent}`}>{fmtGBP(money?.mean)}</p>
    <p className="mt-0.5 text-xs text-ek-ink/65">
      range {fmtGBP(money?.p10)} – {fmtGBP(money?.p90)} <span className="text-ek-ink/30">(P10–P90)</span>
    </p>
  </div>
);

const Spark = ({ values = [] }) => {
  if (!values.length) return null;
  const max = Math.max(...values), min = Math.min(...values);
  const range = max - min || 1;
  const pts = values
    .map((v, i) => `${(i / (values.length - 1)) * 100},${28 - ((v - min) / range) * 24}`)
    .join(" ");
  const rising = values[values.length - 1] >= values[0];
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none" className="h-7 w-24">
      <polyline points={pts} fill="none" strokeWidth="2"
        stroke={rising ? "#dc2626" : "#059669"} vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const TrendIcon = ({ trend }) => {
  if (trend === "accelerating" || trend === "rising")
    return <ArrowUpRight size={14} className="text-red-600" />;
  if (trend === "improving") return <ArrowDownRight size={14} className="text-emerald-600" />;
  return <Minus size={14} className="text-ek-ink/30" />;
};

const Stat = ({ label, value, sub, tone = "text-ek-ink", tipKey }) => (
  <div className="rounded-md border border-black/5 bg-white p-4">
    <p className="text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">
      {tipKey ? <GlossaryTerm k={tipKey} label={label} /> : label}
    </p>
    <p className={`mt-1 font-display text-xl font-bold ${tone}`}>{value}</p>
    {sub && <p className="mt-0.5 text-xs text-ek-ink/65">{sub}</p>}
  </div>
);

// ---------------------------------------------------------------------------
// Per-risk velocity drill-down — 60-day trajectory + breach projection
// ---------------------------------------------------------------------------
const VelocityDetailSheet = ({ riskId, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!riskId) return;
    setD(null); setErr(false);
    api.get(`/foresight/velocity/${riskId}`).then(({ data }) => setD(data)).catch(() => setErr(true));
  }, [riskId]);
  const chart = d?.points?.map((p) => ({ date: p.date.slice(5), Residual: p.residual_score }));
  return (
    <Sheet open={!!riskId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="velocity-detail-sheet">
        {!d && !err && <p className="mt-10 text-sm text-ek-ink/65">Loading trajectory…</p>}
        {err && <p className="mt-10 text-sm text-ek-ink/65">Velocity detail unavailable.</p>}
        {d && (
          <>
            <SheetHeader>
              <SheetTitle className="pr-6 text-left text-base" data-testid="velocity-detail-title">{d.title}</SheetTitle>
              <SheetDescription className="text-left">
                60-day residual-score trajectory with linear breach projection.
              </SheetDescription>
            </SheetHeader>
            <div className="mt-5 grid grid-cols-3 gap-3">
              <Stat label="Velocity" value={`${d.velocity_per_week > 0 ? "+" : ""}${d.velocity_per_week}/wk`}
                tipKey="velocity"
                tone={d.velocity_per_week > 0 ? "text-red-600" : d.velocity_per_week < 0 ? "text-emerald-600" : "text-ek-ink"} />
              <Stat label="Acceleration" value={d.acceleration} tipKey="Acceleration" />
              <Stat label="Days to breach" value={d.days_to_breach != null ? `~${d.days_to_breach}d` : "—"}
                tipKey="Days to breach"
                tone={d.days_to_breach != null ? "text-red-600" : "text-ek-ink/65"} />
            </div>
            <div className="mt-5 h-60 rounded-md border border-black/5 bg-white p-4" data-testid="velocity-detail-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chart} margin={{ top: 6, right: 12, bottom: 0, left: 4 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" minTickGap={28} />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" width={28} domain={[0, 25]} />
                  <RTooltip />
                  <ReferenceLine y={d.tolerance_residual} stroke="#dc2626" strokeDasharray="4 4"
                    label={{ value: "tolerance", fontSize: 10, fill: "#dc2626", position: "insideTopRight" }} />
                  <Line type="monotone" dataKey="Residual" stroke="#7737F6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-3 text-xs text-ek-ink/65">
              Current residual <b>{d.residual_score}</b> vs appetite tolerance <b>{d.tolerance_residual}</b> · trend{" "}
              <span className="capitalize">{d.trend}</span>{d.already_breached ? " · already over appetite" : ""}.
            </p>
            <TrustEnvelope trust={d.trust} testid="velocity-detail-trust" />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ===========================================================================
// 1. VELOCITY
// ===========================================================================
const VelocityPanel = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [selRisk, setSelRisk] = useState(null);
  useEffect(() => {
    api.get("/foresight/velocity").then(({ data }) => setData(data)).catch(() => setErr(true));
  }, []);
  if (err) return <p className="text-sm text-ek-ink/65">Velocity unavailable.</p>;
  if (!data) return <p className="text-sm text-ek-ink/65">Computing risk velocity…</p>;

  const dirTone = data.direction === "worsening" ? "text-red-600"
    : data.direction === "improving" ? "text-emerald-600" : "text-ek-ink/60";
  return (
    <div data-testid="foresight-velocity">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-md border border-black/5 bg-white p-4">
          <p className="text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">Portfolio direction</p>
          <p className={`mt-1 font-display text-2xl font-bold capitalize ${dirTone}`}>{data.direction}</p>
          <p className="mt-0.5 text-xs text-ek-ink/65">{data.portfolio_velocity_per_week > 0 ? "+" : ""}{data.portfolio_velocity_per_week} residual pts/week</p>
        </div>
        <div className="rounded-md border border-black/5 bg-white p-4">
          <p className="text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">Accelerating</p>
          <p className="mt-1 font-display text-2xl font-bold text-orange-600">{data.counts.accelerating}</p>
          <p className="mt-0.5 text-xs text-ek-ink/65">risks gaining speed</p>
        </div>
        <div className="rounded-md border border-black/5 bg-white p-4">
          <p className="text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">Improving</p>
          <p className="mt-1 font-display text-2xl font-bold text-emerald-600">{data.counts.improving}</p>
          <p className="mt-0.5 text-xs text-ek-ink/65">risks trending down</p>
        </div>
      </div>

      {data.imminent_breaches?.length > 0 && (
        <div className="mt-6">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ek-ink">
            <Clock size={15} className="text-red-600" /> Projected appetite breaches
          </h3>
          <div className="mt-3 space-y-2" data-testid="velocity-imminent">
            {data.imminent_breaches.map((r) => (
              <button
                key={r.risk_id}
                onClick={() => setSelRisk(r.risk_id)}
                data-testid={`velocity-imminent-${r.risk_id}`}
                className="flex w-full items-center justify-between gap-3 rounded-md border border-red-100 bg-red-50/60 px-4 py-2.5 text-left transition-colors hover:bg-red-50"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ek-ink">{r.title}</p>
                  <p className="text-xs text-ek-ink/65">residual {r.residual_score} → tolerance {r.tolerance_residual}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Spark values={r.spark} />
                  <span className="whitespace-nowrap rounded-full bg-red-600 px-2.5 py-1 text-xs font-bold text-white">
                    ~{r.days_to_breach}d
                  </span>
                  <ChevronRight size={15} className="text-ek-ink/30" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ek-ink">
          <TrendingUp size={15} className="text-ek-purple" /> Fastest-moving risks
        </h3>
        <div className="mt-3 overflow-hidden rounded-md border border-black/5">
          <table className="w-full text-sm" data-testid="velocity-movers">
            <tbody className="divide-y divide-black/5">
              {data.top_movers.map((r) => (
                <tr
                  key={r.risk_id}
                  onClick={() => setSelRisk(r.risk_id)}
                  data-testid={`velocity-mover-${r.risk_id}`}
                  className="cursor-pointer bg-white transition-colors hover:bg-ek-canvas/40"
                >
                  <td className="px-4 py-2.5"><span className="line-clamp-1 font-medium text-ek-ink">{r.title}</span></td>
                  <td className="px-3 py-2.5"><Spark values={r.spark} /></td>
                  <td className="px-3 py-2.5 text-right">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-ek-ink/70">
                      <TrendIcon trend={r.trend} />{r.velocity_per_week > 0 ? "+" : ""}{r.velocity_per_week}/wk
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="rounded-full bg-ek-canvas px-2 py-0.5 text-[11px] capitalize text-ek-ink/60">{r.trend}</span>
                      <ChevronRight size={14} className="text-ek-ink/25" />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <TrustEnvelope trust={data.trust} testid="velocity-trust" />
      <VelocityDetailSheet riskId={selRisk} onClose={() => setSelRisk(null)} />
    </div>
  );
};

// ===========================================================================
// 2. COUNTERFACTUAL
// ===========================================================================
const CounterfactualPanel = () => {
  const [controls, setControls] = useState([]);
  const [controlId, setControlId] = useState("");
  const [months, setMonths] = useState(6);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/foresight/counterfactual/controls")
      .then(({ data }) => { setControls(data.controls); if (data.controls[0]) setControlId(data.controls[0].control_id); })
      .catch(() => {});
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      const body = { scope: "portfolio", months_ago: months };
      if (controlId) body.control_id = controlId; else body.reduction = 0.4;
      const { data } = await api.post("/foresight/counterfactual", body);
      setResult(data);
    } catch {
      toast.error("Counterfactual failed");
    } finally { setLoading(false); }
  };

  const chartData = result?.trajectory?.map((p) => ({
    date: p.date.slice(0, 7),
    Actual: p.actual_ale_gbp,
    Counterfactual: p.counterfactual_ale_gbp,
  }));

  return (
    <div data-testid="foresight-counterfactual">
      <p className="text-sm text-ek-ink/65">
        Replay history: had a control been matured earlier, what would today&apos;s £ exposure be? Ties to the
      patent-filed replay engine — every figure shown with its confidence band.
      </p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-ek-ink/60">Control</label>
          <select
            data-testid="cf-control-select"
            value={controlId}
            onChange={(e) => setControlId(e.target.value)}
            className="w-72 rounded-[4px] border border-black/10 bg-white px-3 py-2 text-sm text-ek-ink outline-none focus:border-ek-purple"
          >
            {controls.map((c) => (
              <option key={c.control_id} value={c.control_id}>{c.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ek-ink/60">Applied</label>
          <select
            data-testid="cf-months-select"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="rounded-[4px] border border-black/10 bg-white px-3 py-2 text-sm text-ek-ink outline-none focus:border-ek-purple"
          >
            {[3, 6, 9, 12].map((m) => <option key={m} value={m}>{m} months ago</option>)}
          </select>
        </div>
        <button onClick={run} disabled={loading} data-testid="cf-run-button" className="ek-btn disabled:opacity-50">
          <Rewind size={16} /> {loading ? "Replaying…" : "Replay"}
        </button>
      </div>

      {result && (
        <div className="mt-6" data-testid="cf-result">
          <div className="grid gap-4 sm:grid-cols-3">
            <MoneyRange label="Actual today" money={result.ale_actual_today} accent="text-ek-ink" testid="cf-actual" />
            <MoneyRange label="Counterfactual today" money={result.ale_counterfactual_today} accent="text-emerald-600" testid="cf-cf" tipKey="counterfactual" />
            <MoneyRange label="Annual £ avoided" money={result.annual_delta} accent="text-ek-purple" testid="cf-delta" />
          </div>
          {chartData && (
            <div className="mt-5 h-56 rounded-md border border-black/5 bg-white p-4" data-testid="cf-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 6, right: 12, bottom: 0, left: 4 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis tickFormatter={fmtGBP} tick={{ fontSize: 11 }} stroke="#9ca3af" width={48} />
                  <RTooltip formatter={(v) => fmtGBP(v)} />
                  <Line type="monotone" dataKey="Actual" stroke="#1a1a1a" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Counterfactual" stroke="#7737F6" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="mt-4 rounded-md border border-ek-purple/15 bg-ek-purple/5 px-4 py-3 text-sm text-ek-ink/75" data-testid="cf-narrative">
            {result.narrative}
          </p>
          <TrustEnvelope trust={result.trust} testid="cf-trust" />
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// 3. CONTAGION
// ===========================================================================
const ContagionPanel = ({ canSave }) => {
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [board, setBoard] = useState({ pinned: [], items: [], as_of: null, pinned_by: null });
  const [savingBoard, setSavingBoard] = useState(false);
  const [rotIdx, setRotIdx] = useState(0);
  const [compareIds, setCompareIds] = useState([]);  // up to 2 ids for side-by-side

  useEffect(() => {
    api.get("/foresight/contagion/scenarios").then(({ data }) => setScenarios(data.scenarios)).catch(() => {});
    api.get("/foresight/board-scenarios").then(({ data }) => setBoard(data)).catch(() => {});
  }, []);

  // Auto-rotate the board headline every 9 seconds when ≥2 pinned scenarios.
  useEffect(() => {
    if (!board.items || board.items.length < 2) return;
    const t = setInterval(() => setRotIdx((i) => (i + 1) % board.items.length), 9000);
    return () => clearInterval(t);
  }, [board.items]);

  const toggle = async (id) => {
    if (!canSave) return;
    const next = board.pinned.includes(id)
      ? board.pinned.filter((p) => p !== id)
      : board.pinned.length < 5 ? [...board.pinned, id] : null;
    if (next === null) { toast.error("Maximum 5 pinned scenarios."); return; }
    setSavingBoard(true);
    try {
      const { data } = await api.put("/foresight/board-scenarios", { pinned: next });
      setBoard(data);
      toast.success(next.length === 0 ? "Board picks cleared" : `Board picks updated (${next.length})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update board picks");
    } finally { setSavingBoard(false); }
  };

  const simulate = async (id) => {
    setActive(id); setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/foresight/contagion/simulate", { scenario_id: id });
      setResult(data);
    } catch {
      toast.error("Simulation failed");
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      const { data } = await api.post("/foresight/contagion/save", { scenario_id: active });
      toast.success(`Saved as replayable assurance · ${data.run_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const headline = board.items[rotIdx];

  return (
    <div data-testid="foresight-contagion">
      <p className="text-sm text-ek-ink/65">
        Propagate a board-level shock n-tier through the supplier graph to impacted risks — quantified in £
        with a confidence band, then push to action and save as evidence.
      </p>

      {board.items && board.items.length > 0 && (
        <div className="mt-5 rounded-lg border border-ek-purple/30 bg-gradient-to-br from-ek-purple/5 to-white p-5"
          data-testid="board-scenarios-panel">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-ek-purple/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ek-purple">
                Pinned by the board
              </span>
              <span className="text-xs text-ek-ink/60">
                {board.items.length}/5 · rotating {Math.min(rotIdx + 1, board.items.length)}/{board.items.length}
              </span>
            </div>
            <span className="text-xs text-ek-ink/55" data-testid="board-as-of">
              As of {board.as_of ? new Date(board.as_of).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}
              {board.pinned_by ? ` · ${board.pinned_by}` : ""}
            </span>
          </div>
          {headline && (
            <div className="mt-3" data-testid="board-headline">
              <p className="font-display text-xl font-bold text-ek-ink">{headline.name}</p>
              <p className="mt-1 text-xs text-ek-ink/65">{headline.dora_ref}</p>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <div className="rounded-md bg-white px-3 py-2 ring-1 ring-black/5">
                  <p className="text-[10px] uppercase tracking-wide text-ek-ink/55">Annual £ uplift (mean)</p>
                  <p className="text-base font-bold text-red-600">{fmtGBP(headline.ale_uplift?.mean)}</p>
                  <p className="text-[10px] text-ek-ink/55">P10–P90 {fmtGBP(headline.ale_uplift?.p10)}–{fmtGBP(headline.ale_uplift?.p90)}</p>
                </div>
                <div className="rounded-md bg-white px-3 py-2 ring-1 ring-black/5">
                  <p className="text-[10px] uppercase tracking-wide text-ek-ink/55">Posture impact</p>
                  <p className="text-base font-bold text-orange-600">+{headline.posture_delta}</p>
                  <p className="text-[10px] text-ek-ink/55">index points</p>
                </div>
                <div className="rounded-md bg-white px-3 py-2 ring-1 ring-black/5">
                  <p className="text-[10px] uppercase tracking-wide text-ek-ink/55">Risks impacted</p>
                  <p className="text-base font-bold text-ek-ink">{headline.impacted_risk_count}</p>
                  <p className="text-[10px] text-ek-ink/55">across {headline.cascade_tiers} tier(s)</p>
                </div>
              </div>
            </div>
          )}
          {board.items.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {board.items.map((it, i) => (
                <button key={it.scenario_id} onClick={() => setRotIdx(i)}
                  data-testid={`board-rotate-${it.scenario_id}`}
                  className={`rounded-full px-2.5 py-0.5 text-[11px] transition ${
                    i === rotIdx
                      ? "bg-ek-purple text-white"
                      : "bg-white text-ek-ink/70 ring-1 ring-black/5 hover:bg-ek-purple/10"
                  }`}>{it.name}</button>
              ))}
            </div>
          )}
          {board.items.length >= 2 && (
            <div className="mt-4 border-t border-ek-purple/15 pt-3">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-ek-purple">Compare</span>
                <span className="text-[11px] text-ek-ink/60">
                  {compareIds.length === 0 && "tap two scenarios to compare side-by-side"}
                  {compareIds.length === 1 && "pick one more…"}
                  {compareIds.length === 2 && (
                    <button onClick={() => setCompareIds([])} data-testid="board-compare-clear"
                      className="underline hover:text-ek-purple">clear</button>
                  )}
                </span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {board.items.map((it) => {
                  const picked = compareIds.includes(it.scenario_id);
                  return (
                    <button key={it.scenario_id}
                      data-testid={`board-compare-pick-${it.scenario_id}`}
                      onClick={() => setCompareIds((cur) =>
                        cur.includes(it.scenario_id)
                          ? cur.filter((x) => x !== it.scenario_id)
                          : cur.length < 2 ? [...cur, it.scenario_id] : cur)}
                      className={`rounded-full px-2.5 py-0.5 text-[11px] transition ring-1 ${
                        picked
                          ? "bg-amber-500 text-white ring-amber-600"
                          : "bg-white text-ek-ink/70 ring-black/10 hover:bg-amber-50"
                      }`}>{it.name}</button>
                  );
                })}
              </div>
              {compareIds.length === 2 && (() => {
                const A = board.items.find((x) => x.scenario_id === compareIds[0]);
                const B = board.items.find((x) => x.scenario_id === compareIds[1]);
                if (!A || !B) return null;
                const worse = (A.ale_uplift?.mean || 0) >= (B.ale_uplift?.mean || 0) ? A : B;
                return (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="board-compare-result">
                    {[A, B].map((it) => (
                      <div key={it.scenario_id}
                        className={`rounded-md bg-white p-3 ring-1 ${it === worse ? "ring-red-300 bg-red-50/40" : "ring-black/10"}`}>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-ek-ink">{it.name}</p>
                          {it === worse && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">Worse for us</span>}
                        </div>
                        <p className="mt-1 text-[10px] text-ek-ink/55">{it.dora_ref}</p>
                        <div className="mt-2 grid grid-cols-3 gap-1.5 text-[10px]">
                          <div><p className="text-ek-ink/55">£ uplift</p><p className="text-sm font-bold text-red-600">{fmtGBP(it.ale_uplift?.mean)}</p></div>
                          <div><p className="text-ek-ink/55">Posture</p><p className="text-sm font-bold text-orange-600">+{it.posture_delta}</p></div>
                          <div><p className="text-ek-ink/55">Risks</p><p className="text-sm font-bold text-ek-ink">{it.impacted_risk_count}</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="contagion-scenarios">
        {scenarios.map((s) => {
          const pinned = board.pinned.includes(s.id);
          return (
          <div
            key={s.id}
            className={`relative rounded-md border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
              active === s.id ? "border-ek-purple bg-ek-purple/5" : pinned ? "border-ek-purple/40 bg-ek-purple/[0.03]" : "border-black/5 bg-white"
            }`}
          >
            {canSave && (
              <button
                onClick={(e) => { e.stopPropagation(); toggle(s.id); }}
                disabled={savingBoard}
                data-testid={`board-pin-${s.id}`}
                aria-label={pinned ? "Unpin from board" : "Pin to board"}
                title={pinned ? "Unpin from board" : "Pin to board"}
                className={`absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs transition disabled:opacity-50 ${
                  pinned ? "bg-ek-purple text-white" : "bg-white text-ek-ink/40 ring-1 ring-black/10 hover:text-ek-purple"
                }`}
              ><Pin size={12} /></button>
            )}
            <button
              onClick={() => simulate(s.id)}
              data-testid={`contagion-scenario-${s.id}`}
              className="block w-full text-left"
            >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-ek-purple">{s.category}</span>
              <span className="rounded-full bg-ek-canvas px-2 py-0.5 text-[10px] text-ek-ink/65">sev {Math.round(s.severity * 100)}%</span>
            </div>
            <p className="mt-1.5 text-sm font-semibold text-ek-ink">{s.name}</p>
            <p className="mt-1 line-clamp-2 text-xs text-ek-ink/65">{s.description}</p>
            {s.dora_ref && <p className="mt-2 text-[11px] font-medium text-ek-ink/65">↳ {s.dora_ref}</p>}
            </button>
          </div>
          );
        })}
      </div>

      {loading && <p className="mt-5 text-sm text-ek-ink/65">Cascading the shock through the supply graph…</p>}

      {result && !loading && (
        <div className="mt-6" data-testid="contagion-result">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MoneyRange label="Annual £ loss uplift" money={result.ale_uplift} accent="text-red-600" testid="contagion-uplift" />
            <div className="rounded-md border border-black/5 bg-white p-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">Posture impact</p>
              <p className="mt-1 font-display text-2xl font-bold text-orange-600">+{result.posture_delta}</p>
              <p className="mt-0.5 text-xs text-ek-ink/65">index points</p>
            </div>
            <div className="rounded-md border border-black/5 bg-white p-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">Suppliers hit</p>
              <p className="mt-1 font-display text-2xl font-bold text-ek-ink">{result.impacted_supplier_count}</p>
              <p className="mt-0.5 text-xs text-ek-ink/65">across {result.cascade_tiers} tier(s)</p>
            </div>
            <div className="rounded-md border border-black/5 bg-white p-4">
              <p className="text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">Risks impacted</p>
              <p className="mt-1 font-display text-2xl font-bold text-ek-ink">{result.impacted_risk_count}</p>
              <p className="mt-0.5 text-xs text-ek-ink/65">elevated exposure</p>
            </div>
          </div>

          <p className="mt-4 rounded-md border border-red-100 bg-red-50/60 px-4 py-3 text-sm text-ek-ink/75" data-testid="contagion-narrative">
            {result.narrative}
          </p>

          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-ek-ink">
                <Zap size={15} className="text-red-600" /> Most-impacted risks
              </h3>
              <div className="mt-3 space-y-2" data-testid="contagion-risks">
                {result.impacted_risks.slice(0, 6).map((r) => (
                  <div key={r.risk_id} className="flex items-center justify-between gap-3 rounded-md border border-black/5 bg-white px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ek-ink">{r.title}</p>
                      <p className="text-xs text-ek-ink/65">{r.driver}</p>
                    </div>
                    <span className="whitespace-nowrap text-sm font-semibold text-red-600">+{fmtGBP(r.ale_uplift_gbp)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-ek-ink">
                <Network size={15} className="text-ek-purple" /> Recommended actions
              </h3>
              <div className="mt-3 space-y-2" data-testid="contagion-actions">
                {result.recommended_actions.map((a, i) => (
                  <div key={i} className="rounded-md border border-black/5 bg-white px-4 py-2.5">
                    <p className="truncate text-sm font-medium text-ek-ink">{a.suggested_playbook}</p>
                    <p className="text-xs text-ek-ink/65">{a.title} · owner: {a.owner}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {canSave && (
            <button onClick={save} disabled={saving} data-testid="contagion-save-button" className="ek-btn-outline mt-5 text-sm disabled:opacity-50">
              <Save size={15} /> {saving ? "Saving…" : "Save as replayable assurance"}
            </button>
          )}
          <TrustEnvelope trust={result.trust} testid="contagion-trust" />
        </div>
      )}
    </div>
  );
};

// ===========================================================================
// 4. RUNS — replayable assurance evidence viewer
// ===========================================================================
const RunDetailSheet = ({ runId, onClose }) => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!runId) return;
    setD(null); setErr(false);
    api.get(`/foresight/runs/${runId}`).then(({ data }) => setD(data)).catch(() => setErr(true));
  }, [runId]);
  return (
    <Sheet open={!!runId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl" data-testid="run-detail-sheet">
        {!d && !err && <p className="mt-10 text-sm text-ek-ink/65">Loading run…</p>}
        {err && <p className="mt-10 text-sm text-ek-ink/65">Run unavailable.</p>}
        {d && (
          <>
            <SheetHeader>
              <SheetTitle className="pr-6 text-left text-base" data-testid="run-detail-title">{d.name}</SheetTitle>
              <SheetDescription className="text-left">
                Replayable assurance evidence · saved {new Date(d.created_at).toLocaleString()}
                {d.created_by_name ? ` by ${d.created_by_name}` : ""}
              </SheetDescription>
            </SheetHeader>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <MoneyRange label="Annual £ loss uplift" money={d.ale_uplift} accent="text-red-600" testid="run-uplift" />
              <Stat label="Posture impact" value={`+${d.posture_delta}`} sub="index points" tone="text-orange-600" />
              <Stat label="Suppliers / risks hit" value={`${d.impacted_supplier_count} / ${d.impacted_risk_count}`}
                sub={`across ${d.cascade_tiers ?? "—"} tier(s)`} />
            </div>
            {d.dora_ref && <p className="mt-3 text-[11px] font-medium text-ek-ink/65">↳ {d.dora_ref}</p>}
            {d.snapshot?.narrative && (
              <p className="mt-4 rounded-md border border-red-100 bg-red-50/60 px-4 py-3 text-sm text-ek-ink/75" data-testid="run-narrative">
                {d.snapshot.narrative}
              </p>
            )}
            {d.snapshot?.impacted_risks?.length > 0 && (
              <div className="mt-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-ek-ink">
                  <Zap size={15} className="text-red-600" /> Most-impacted risks
                </h3>
                <div className="mt-3 space-y-2" data-testid="run-risks">
                  {d.snapshot.impacted_risks.slice(0, 8).map((r) => (
                    <div key={r.risk_id} className="flex items-center justify-between gap-3 rounded-md border border-black/5 bg-white px-4 py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ek-ink">{r.title}</p>
                        <p className="text-xs text-ek-ink/65">{r.driver}</p>
                      </div>
                      <span className="whitespace-nowrap text-sm font-semibold text-red-600">+{fmtGBP(r.ale_uplift_gbp)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {d.snapshot?.recommended_actions?.length > 0 && (
              <div className="mt-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-ek-ink">
                  <Network size={15} className="text-ek-purple" /> Recommended actions
                </h3>
                <div className="mt-3 space-y-2" data-testid="run-actions">
                  {d.snapshot.recommended_actions.map((a, i) => (
                    <div key={i} className="rounded-md border border-black/5 bg-white px-4 py-2.5">
                      <p className="truncate text-sm font-medium text-ek-ink">{a.suggested_playbook}</p>
                      <p className="text-xs text-ek-ink/65">{a.title} · owner: {a.owner}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <TrustEnvelope trust={d.trust} testid="run-detail-trust" />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const RunsPanel = () => {
  const [runs, setRuns] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    api.get("/foresight/runs").then(({ data }) => setRuns(data.items)).catch(() => setRuns([]));
  }, []);
  if (!runs) return <p className="text-sm text-ek-ink/65">Loading saved runs…</p>;
  return (
    <div data-testid="foresight-runs">
      <p className="text-sm text-ek-ink/65">
        Saved contagion simulations kept as replayable, time-stamped assurance evidence — click any run to
        inspect its full impact snapshot for audit and regulator engagement.
      </p>
      {runs.length === 0 ? (
        <div className="mt-5 rounded-md border border-dashed border-black/10 bg-white p-8 text-center" data-testid="runs-empty">
          <History size={22} className="mx-auto text-ek-ink/25" />
          <p className="mt-3 text-sm font-medium text-ek-ink/60">No saved runs yet</p>
          <p className="mt-1 text-xs text-ek-ink/65">Run a scenario in the Contagion tab and choose “Save as replayable assurance”.</p>
        </div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="runs-list">
          {runs.map((r) => (
            <button
              key={r.run_id}
              onClick={() => setSel(r.run_id)}
              data-testid={`run-row-${r.run_id}`}
              className="flex w-full items-center justify-between gap-3 rounded-md border border-black/5 bg-white px-4 py-3 text-left transition-colors hover:bg-ek-canvas/40"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ek-ink">{r.name}</p>
                <p className="text-xs text-ek-ink/65">
                  {new Date(r.created_at).toLocaleString()} · {r.impacted_supplier_count} suppliers · {r.impacted_risk_count} risks
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-sm font-semibold text-red-600">+{fmtGBP(r.ale_uplift?.mean)}</span>
                <ChevronRight size={15} className="text-ek-ink/30" />
              </div>
            </button>
          ))}
        </div>
      )}
      <RunDetailSheet runId={sel} onClose={() => setSel(null)} />
    </div>
  );
};

// ===========================================================================
// Foresight — parent with header chips + sub-tabs
// ===========================================================================
const subTabCls =
  "rounded-sm px-4 py-1.5 text-sm font-medium text-ek-ink/65 data-[state=active]:bg-ek-purple data-[state=active]:text-white transition-colors";

export const Foresight = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const canSave = hasPermission(user, "risk.write");

  useEffect(() => {
    api.get("/foresight/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="foresight-root">
      {summary && (
        <div className="mb-6 grid gap-3 sm:grid-cols-3" data-testid="foresight-chips">
          <div className="rounded-md border border-black/5 bg-white p-4">
            <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">
              <Gauge size={13} /> Momentum
            </p>
            <p className={`mt-1 font-display text-xl font-bold capitalize ${summary.velocity.direction === "worsening" ? "text-red-600" : summary.velocity.direction === "improving" ? "text-emerald-600" : "text-ek-ink"}`}>
              {summary.velocity.direction}
            </p>
            <p className="mt-0.5 text-xs text-ek-ink/65">{summary.velocity.accelerating} accelerating{summary.velocity.next_breach ? ` · next breach ~${summary.velocity.next_breach.days_to_breach}d` : ""}</p>
          </div>
          <div className="rounded-md border border-black/5 bg-white p-4">
            <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">
              <Rewind size={13} /> Best opportunity
            </p>
            <p className="mt-1 line-clamp-1 font-display text-base font-bold text-ek-purple">
              {summary.counterfactual_opportunity?.title || "—"}
            </p>
            <p className="mt-0.5 text-xs text-ek-ink/65">highest-leverage control to mature</p>
          </div>
          <div className="rounded-md border border-black/5 bg-white p-4">
            <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ek-ink/65">
              <AlertTriangle size={13} /> Headline scenario
            </p>
            <p className="mt-1 line-clamp-1 font-display text-base font-bold text-ek-ink">{summary.headline_contagion.name}</p>
            <p className="mt-0.5 text-xs text-red-600">+{fmtGBP(summary.headline_contagion.ale_uplift?.mean)} exposure</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="velocity">
        <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-md border border-black/5 bg-white p-1" data-testid="foresight-subtabs">
          <TabsTrigger value="velocity" className={subTabCls} data-testid="foresight-subtab-velocity">
            <TrendingUp size={14} className="mr-1.5" /> Velocity
          </TabsTrigger>
          <TabsTrigger value="counterfactual" className={subTabCls} data-testid="foresight-subtab-counterfactual">
            <Rewind size={14} className="mr-1.5" /> Counterfactual
          </TabsTrigger>
          <TabsTrigger value="contagion" className={subTabCls} data-testid="foresight-subtab-contagion">
            <GitBranch size={14} className="mr-1.5" /> Contagion
          </TabsTrigger>
          <TabsTrigger value="runs" className={subTabCls} data-testid="foresight-subtab-runs">
            <History size={14} className="mr-1.5" /> Runs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="velocity" className="mt-5 focus:outline-none"><VelocityPanel /></TabsContent>
        <TabsContent value="counterfactual" className="mt-5 focus:outline-none"><CounterfactualPanel /></TabsContent>
        <TabsContent value="contagion" className="mt-5 focus:outline-none"><ContagionPanel canSave={canSave} /></TabsContent>
        <TabsContent value="runs" className="mt-5 focus:outline-none"><RunsPanel /></TabsContent>
      </Tabs>
    </div>
  );
};
